// src/pages/RoutePage.jsx
//
// Road route to a well pad. Built for 390x844 FIRST — this is the only view in
// the app targeted at a phone, so there is no desktop layout being narrowed and
// no breakpoint being inherited. 768x1024 widens the sheet and nothing else.
//
// FIELD CONSTRAINTS, which drive most of what looks unusual here:
//   - Sunlight. High contrast, heavy weights, no mid-grey text on white.
//   - Gloves and movement. Every control is at least 44x44.
//   - No hover. Nothing is revealed by hovering, because nothing hovers on a
//     phone; state that would live in a tooltip is on screen or absent.
//   - Starlink drops mid-use. See useRoutingStore for what survives it.
//
// ROUNDING HAPPENS HERE AND NOWHERE ELSE. The store holds raw metres; every
// figure on screen goes through lib/routeFormat.js, which renders from the
// API's own distances rather than from the geometry. See that module for why
// the two differ.

import { useEffect, useRef, useState, useCallback } from "react";
import maplibregl from "maplibre-gl";
import { whenMapLoaded } from "../lib/map/whenMapLoaded";
import { MapPin, Crosshair, X, DoorOpen, AlertTriangle, Loader2, ArrowLeft } from "lucide-react";
import { useAppNav } from "../hooks/useAppNav";
import { useRoutingStore } from "../stores/useRoutingStore";
import { useAuthStore } from "../stores/useAuthStore";
import { buildTransformRequest } from "../api/mapTransformRequest";
import { basemapTileUrl, useBasemapStore } from "../stores/useBasemapStore";
import { attachAttribution, basemapAttribution } from "../lib/map/attribution";
import { formatTotalDistance, formatStepDistance, maneuverLabel, formatAge } from "../lib/routeFormat";
import { refusalCopy, ADVISORY } from "../components/routing/refusalCopy";
import { useFieldImagery } from "../hooks/useFieldImagery";
import FieldImageryToggle from "../components/field/FieldImageryToggle";

const PAD_TILES = `${window.location.origin}/vtiles/well_pad/{z}/{x}/{y}.pbf`;

const SRC_ROUTE = "route-line";
const SRC_PADS = "route-pads";
const SRC_PINS = "route-pins";
const L_CASING = "route-casing";
const L_LINE = "route-line";
const L_ACTIVE = "route-active";
const L_PADS_FILL = "route-pads-fill";
const L_PADS_LINE = "route-pads-line";
const L_PINS = "route-pins";

const EMPTY_FC = { type: "FeatureCollection", features: [] };

// Sheet heights as a fraction of viewport. Three stops, not a free drag: a
// continuous sheet on a phone in a truck ends up somewhere useless.
const STOPS = [0.28, 0.55, 0.88];

export default function RoutePage() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const didInitRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const [pickMode, setPickMode] = useState(null); // 'origin' | null
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoError, setGeoError] = useState(null);
  const [stop, setStop] = useState(1);
  const dragRef = useRef(null);

  const basemapId = useBasemapStore((s) => s.activeBasemapId);
  const nav = useAppNav();

  // Imagery, shared with /field/site. The hook owns the raster layer on this
  // map and the three-state answer the toggle renders.
  const imagery = useFieldImagery(mapRef, mapReady);

  const origin = useRoutingStore((s) => s.origin);
  const destination = useRoutingStore((s) => s.destination);
  const route = useRoutingStore((s) => s.route);
  const loading = useRoutingStore((s) => s.loading);
  const error = useRoutingStore((s) => s.error);
  const isStale = useRoutingStore((s) => s.isStale);
  const staleFetchedAt = useRoutingStore((s) => s.staleFetchedAt);
  const activeStepIndex = useRoutingStore((s) => s.activeStepIndex);
  const setOrigin = useRoutingStore((s) => s.setOrigin);
  const setDestination = useRoutingStore((s) => s.setDestination);
  const setActiveStep = useRoutingStore((s) => s.setActiveStep);
  const requestRoute = useRoutingStore((s) => s.requestRoute);
  const clear = useRoutingStore((s) => s.clear);

  const steps = route?.routes?.[0]?.legs?.[0]?.steps ?? [];
  const total = route?.routes?.[0]?.distance;
  const gateCount = route?.gates ?? steps.filter((s) => s.gate).length;

  // ── map ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (didInitRef.current || !containerRef.current) return;
    didInitRef.current = true;

    const map = new maplibregl.Map({
      container: containerRef.current,
      // Raster basemap only. This view needs a legible ground reference, not
      // the full styled stack MapView builds.
      style: {
        version: 8,
        sources: {
          base: { type: "raster", tiles: [basemapTileUrl(basemapId)], tileSize: 256, attribution: basemapAttribution(basemapId) },
        },
        layers: [{ id: "base", type: "raster", source: "base" }],
      },
      center: [-101.44, 31.02],
      zoom: 11,
      attributionControl: { compact: false },
      // /vtiles/ 401s an anonymous caller, and MapLibre does not carry the
      // app's bearer on its own — without this the pad layer silently renders
      // nothing and there is no destination to tap. Shared with every other map
      // in the app on purpose: a second copy of the tile-auth contract is how
      // the two drift (see api/mapTransformRequest.js).
      transformRequest: buildTransformRequest(() => useAuthStore.getState().token),
    });
    // Mapbox's logo and credit (REM-684).
    attachAttribution(map, { mapboxSource: "base" });
    mapRef.current = map;
    // Same handle MapView exposes as window.__mapA. It is what makes this view
    // drivable from a headless browser: without it a verification run can only
    // click blind pixels and hope a pad is under them.
    window.__routeMap = map;

    whenMapLoaded(map, () => {
      map.addSource(SRC_PADS, { type: "vector", tiles: [PAD_TILES], minzoom: 8, maxzoom: 16 });
      map.addLayer({
        // "assets", not "well_pad". The asset_type selects the tile ROUTE
        // (/vtiles/well_pad/...); the MVT layer inside it is named by
        // api.vector_tile's ST_AsMVT(q, 'assets', ...) and is the same for
        // every asset type. Getting this wrong renders nothing and raises
        // nothing — the layer simply matches no features.
        id: L_PADS_FILL, type: "fill", source: SRC_PADS, "source-layer": "assets",
        paint: { "fill-color": "#f59e0b", "fill-opacity": 0.28 },
      });
      map.addLayer({
        id: L_PADS_LINE, type: "line", source: SRC_PADS, "source-layer": "assets",
        paint: { "line-color": "#f59e0b", "line-width": 2 },
      });

      map.addSource(SRC_ROUTE, { type: "geojson", data: EMPTY_FC });
      // Casing beneath the line: a single-width line vanishes against desert
      // caliche in direct sun.
      map.addLayer({
        id: L_CASING, type: "line", source: SRC_ROUTE,
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#0f172a", "line-width": 11, "line-opacity": 0.9 },
      });
      map.addLayer({
        id: L_LINE, type: "line", source: SRC_ROUTE,
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#22d3ee", "line-width": 5 },
      });
      map.addLayer({
        id: L_ACTIVE, type: "line", source: SRC_ROUTE,
        layout: { "line-cap": "round", "line-join": "round" },
        filter: ["==", ["get", "idx"], -1],
        paint: { "line-color": "#fde047", "line-width": 8 },
      });

      map.addSource(SRC_PINS, { type: "geojson", data: EMPTY_FC });
      map.addLayer({
        id: L_PINS, type: "circle", source: SRC_PINS,
        paint: {
          "circle-radius": 9,
          "circle-color": ["case", ["==", ["get", "role"], "origin"], "#22c55e", "#ef4444"],
          "circle-stroke-width": 3, "circle-stroke-color": "#0f172a",
        },
      });
      setMapReady(true);
    });

    return () => { map.remove(); mapRef.current = null; didInitRef.current = false; delete window.__routeMap; };
    // Basemap id is read once at construction; changing it is not a flow this
    // view offers, so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map taps: pick an origin, or pick a pad as destination.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const onClick = (e) => {
      if (pickMode === "origin") {
        setOrigin({ lng: e.lngLat.lng, lat: e.lngLat.lat });
        setPickMode(null);
        return;
      }
      const hits = map.queryRenderedFeatures(e.point, { layers: [L_PADS_FILL] });
      if (hits.length) {
        const f = hits[0];
        const id = f.properties?.id ?? f.id;
        setDestination({
          lng: e.lngLat.lng, lat: e.lngLat.lat,
          padObservationId: typeof id === "string" ? id : undefined,
        });
      }
    };
    map.on("click", onClick);
    return () => map.off("click", onClick);
  }, [mapReady, pickMode, setOrigin, setDestination]);

  // Pins.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const features = [];
    if (origin) features.push({ type: "Feature", properties: { role: "origin" }, geometry: { type: "Point", coordinates: [origin.lng, origin.lat] } });
    if (destination) features.push({ type: "Feature", properties: { role: "destination" }, geometry: { type: "Point", coordinates: [destination.lng, destination.lat] } });
    map.getSource(SRC_PINS)?.setData({ type: "FeatureCollection", features });
  }, [origin, destination, mapReady]);

  // Route line, one feature per step so a step can be highlighted on its own.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource(SRC_ROUTE);
    if (!src) return;

    if (!steps.length) { src.setData(EMPTY_FC); return; }

    const fc = {
      type: "FeatureCollection",
      features: steps.map((s, idx) => ({ type: "Feature", properties: { idx }, geometry: s.geometry })),
    };
    src.setData(fc);

    const b = new maplibregl.LngLatBounds();
    for (const s of steps) for (const c of s.geometry?.coordinates ?? []) b.extend(c);
    if (!b.isEmpty()) {
      // Bottom padding clears the sheet so the route is not drawn underneath it.
      map.fitBounds(b, { padding: { top: 70, left: 40, right: 40, bottom: Math.round((containerRef.current?.clientHeight ?? window.innerHeight) * STOPS[stop]) + 30 }, duration: 500 });
    }
    // `stop` intentionally excluded: refitting on every sheet drag fights the
    // person's own panning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getLayer(L_ACTIVE)) return;
    map.setFilter(L_ACTIVE, ["==", ["get", "idx"], activeStepIndex ?? -1]);
  }, [activeStepIndex, mapReady]);

  useEffect(() => {
    if (origin && destination) requestRoute();
  }, [origin, destination, requestRoute]);

  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) { setGeoError("This device has no location service."); return; }
    setGeoBusy(true); setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoBusy(false);
        setOrigin({ lng: pos.coords.longitude, lat: pos.coords.latitude });
        mapRef.current?.easeTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 13 });
      },
      (err) => {
        setGeoBusy(false);
        setGeoError(err.code === 1
          ? "Location permission is off. Tap the map to set your starting point instead."
          : "Could not get a location fix. Tap the map to set your starting point instead.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  }, [setOrigin]);

  const tapStep = useCallback((i) => {
    setActiveStep(i === activeStepIndex ? null : i);
    const loc = steps[i]?.maneuver?.location;
    if (loc && mapRef.current) mapRef.current.easeTo({ center: loc, zoom: 15, duration: 450 });
  }, [activeStepIndex, setActiveStep, steps]);

  // Sheet drag — pointer events, so touch and mouse are one path.
  const onDragStart = (e) => { dragRef.current = { y: e.clientY, stop }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onDragMove = (e) => {
    if (!dragRef.current) return;
    const dy = dragRef.current.y - e.clientY;
    if (Math.abs(dy) < 40) return;
    const next = Math.max(0, Math.min(STOPS.length - 1, dragRef.current.stop + (dy > 0 ? 1 : -1)));
    setStop(next);
    dragRef.current = null;
  };
  const onDragEnd = () => { dragRef.current = null; };

  const copy = error ? refusalCopy(error, error.body) : null;
  const sheetH = `${Math.round(STOPS[stop] * 100)}%`;
  const dbg = route?._debug; // absent for external callers — guard on presence

  // h-full below, NOT 100dvh. This page renders into AppShell's
  // <main className="flex-1 overflow-hidden"> — already a bounded flex child
  // under a ~100px header and beside a 48px rail. Sizing to the VIEWPORT
  // instead of the parent pushed the sheet 76px past the bottom at 390x844
  // (100px at 768x1024) and took the advisory line — the one element
  // specified as persistent — off screen at both. Sheet stops are
  // percentages of the same parent, for the same reason.
  return (
    <div className="relative w-full h-full overflow-hidden bg-slate-950 text-white select-none">
      <div ref={containerRef} className="absolute inset-0" />

      {/* Top controls. 44pt minimum, opaque, high contrast. */}
      <div className="absolute top-0 left-0 right-0 z-20 p-3 flex gap-2 pointer-events-none">
        {/* THE ONLY WAY OUT. Shell-less, so there is no rail and no header to
            navigate back with — and under Capacitor there is no browser chrome
            either, which is why this cannot lean on history back. Returns to
            /field, the FieldAssist shell, NOT to the platform home: this page
            is a child of that shell now, and back should mean up one level.
            Routed through useAppNav like every other navigation in the app.
            Leftmost and 44pt, but see the duplicate at the foot of the sheet:
            at 390x844 the top of the screen is not thumb-reachable. */}
        <button
          type="button"
          onClick={() => nav.goField()}
          aria-label="Back to FieldAssist"
          className="pointer-events-auto min-h-[44px] min-w-[44px] rounded-xl bg-slate-900 border-2 border-slate-600 flex items-center justify-center active:bg-slate-700 shadow-lg"
        >
          <ArrowLeft size={22} />
        </button>
        <button
          type="button"
          onClick={useMyLocation}
          className="pointer-events-auto min-h-[44px] min-w-[44px] px-4 rounded-xl bg-slate-900 border-2 border-slate-600 font-semibold text-[15px] flex items-center gap-2 active:bg-slate-700 shadow-lg"
        >
          {geoBusy ? <Loader2 size={20} className="animate-spin" /> : <Crosshair size={20} />}
          <span>My location</span>
        </button>
        <button
          type="button"
          onClick={() => setPickMode(pickMode === "origin" ? null : "origin")}
          aria-pressed={pickMode === "origin"}
          className={`pointer-events-auto min-h-[44px] min-w-[44px] px-4 rounded-xl border-2 font-semibold text-[15px] flex items-center gap-2 shadow-lg ${
            pickMode === "origin" ? "bg-cyan-400 text-slate-950 border-cyan-200" : "bg-slate-900 border-slate-600 active:bg-slate-700"
          }`}
        >
          <MapPin size={20} />
          <span>{pickMode === "origin" ? "Tap the map" : "Set start"}</span>
        </button>
        {(origin || destination) && (
          <button
            type="button"
            onClick={() => { clear(); setPickMode(null); }}
            aria-label="Clear route"
            className="pointer-events-auto min-h-[44px] min-w-[44px] rounded-xl bg-slate-900 border-2 border-slate-600 flex items-center justify-center active:bg-slate-700 shadow-lg"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Imagery. ITS OWN ABSOLUTE ELEMENT, not a fourth button in the control
          row above — measured at 390x844, where both alternatives were wrong.
          In the row without wrapping it squeezed "My location" into
          "My locatio…"; with wrapping it dropped onto the hint banner at
          top-[68px] and covered it. top-[124px] clears both, and leaves this
          view's existing layout untouched.

          Same component /field/site uses: one control, one set of states, one
          place a licence explanation can be wrong. */}
      <div className="absolute top-[124px] right-3 z-20 flex justify-end pointer-events-none">
        <FieldImageryToggle imagery={imagery} />
      </div>

      {geoError && (
        <div className="absolute top-[68px] left-3 right-3 z-20 rounded-xl bg-amber-300 text-slate-950 px-4 py-3 text-[15px] font-semibold shadow-lg">
          {geoError}
        </div>
      )}

      {!destination && !error && (
        <div className="absolute top-[68px] left-3 right-3 z-10 rounded-xl bg-slate-900/95 border-2 border-slate-700 px-4 py-3 text-[15px] font-medium shadow-lg">
          {origin ? "Now tap a pad to route to it." : "Set your starting point, then tap a pad."}
        </div>
      )}

      {/* Sheet */}
      <div
        className="absolute left-0 right-0 bottom-0 z-30 bg-slate-900 border-t-2 border-slate-700 rounded-t-2xl shadow-2xl flex flex-col"
        style={{ height: sheetH, transition: "height 180ms ease-out" }}
      >
        <div
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          className="shrink-0 h-11 flex items-center justify-center cursor-grab touch-none"
          role="separator"
          aria-label="Drag to resize"
        >
          <div className="w-12 h-1.5 rounded-full bg-slate-600" />
        </div>

        <div className="px-4 pb-2 shrink-0">
          {loading && (
            <div className="flex items-center gap-2 text-[17px] font-bold">
              <Loader2 size={20} className="animate-spin" /> Finding a route…
            </div>
          )}

          {!loading && route && (
            <>
              <div className="flex items-baseline gap-3">
                <span className="text-[34px] leading-none font-black tabular-nums">{formatTotalDistance(total)}</span>
                <span className="text-[15px] font-semibold text-slate-300">
                  {steps.length} {steps.length === 1 ? "step" : "steps"}
                </span>
                {gateCount > 0 && (
                  <span className="flex items-center gap-1 text-[15px] font-bold text-amber-300">
                    <DoorOpen size={17} /> {gateCount}
                  </span>
                )}
              </div>
              {/* No travel time. The corpus has no speed attribute and a number
                  here would be invented. */}
            </>
          )}

          {!loading && copy && (
            <div
              className={`rounded-xl px-4 py-3 border-2 ${
                copy.tone === "stale" ? "bg-amber-300 text-slate-950 border-amber-200"
                : copy.tone === "neutral" ? "bg-slate-800 text-white border-slate-600"
                : "bg-red-900 text-white border-red-500"
              }`}
            >
              <div className="flex items-start gap-2">
                {copy.tone !== "neutral" && <AlertTriangle size={20} className="shrink-0 mt-0.5" />}
                <div>
                  <div className="font-bold text-[17px] leading-tight">{copy.title}</div>
                  <div className="text-[15px] mt-1 leading-snug">{copy.body}</div>
                  <div className="text-[15px] mt-2 font-semibold leading-snug">{copy.action}</div>
                  {isStale && staleFetchedAt && (
                    <div className="text-[13px] mt-2 font-bold font-mono uppercase tracking-wide">
                      Saved {formatAge(staleFetchedAt)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <ol className="flex-1 overflow-y-auto px-2 pb-2">
          {steps.map((s, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => tapStep(i)}
                aria-current={i === activeStepIndex}
                className={`w-full min-h-[56px] px-3 py-3 rounded-xl mb-1 flex items-center gap-3 text-left border-2 ${
                  i === activeStepIndex ? "bg-yellow-300 text-slate-950 border-yellow-200" : "bg-slate-800 border-transparent active:bg-slate-700"
                }`}
              >
                <span className="shrink-0 w-8 h-8 rounded-lg bg-slate-950/30 flex items-center justify-center font-black text-[15px] tabular-nums">
                  {i + 1}
                </span>
                {/* No road name is rendered — not a name, not a placeholder.
                    The corpus carries none. */}
                <span className="flex-1 font-bold text-[17px] leading-tight">{maneuverLabel(s.maneuver)}</span>
                {s.gate && (
                  <span className="shrink-0 flex items-center gap-1 text-[13px] font-black uppercase px-2 py-1 rounded-md bg-amber-300 text-slate-950">
                    <DoorOpen size={15} /> Gate
                  </span>
                )}
                <span className="shrink-0 font-bold text-[17px] tabular-nums">{formatStepDistance(s.distance)}</span>
              </button>
            </li>
          ))}
        </ol>

        {/* Persistent, and never scrolled away. */}
        <div className="shrink-0 px-4 py-2 border-t-2 border-slate-700 bg-slate-950 text-[13px] font-semibold text-slate-300 leading-snug">
          <button
            type="button"
            onClick={() => nav.goField()}
            className="min-h-[44px] w-full mb-1 rounded-xl bg-slate-800 border-2 border-slate-600 flex items-center justify-center gap-2 font-bold text-[15px] text-white active:bg-slate-700"
          >
            <ArrowLeft size={18} /> Back to FieldAssist
          </button>
          {ADVISORY}
          {dbg && (
            <span className="block mt-1 text-slate-500 font-mono text-[11px]">
              {dbg.path} · {dbg.edges_traversed} edges · {dbg.solve_ms}ms
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
